/**
 * Paired chord/lyric lines, and the edits that move between them.
 *
 * A chord is pinned to a *character index* in its line, never to a column on a
 * grid. That one decision is what lets the words be edited at all: retype a
 * word and every chord after it moves with the text, instead of silently
 * landing on the wrong syllable.
 *
 * Everything here is pure. The component holds the caret; this holds the rules.
 */

export interface Chord {
  /** Character index in `text` where the chord changes. */
  at: number;
  name: string;
}

export interface Line {
  text: string;
  chords: Chord[];
  /** A run with no words — an intro, a turnaround, a solo. */
  instrumental?: boolean;
}

const CHORD_TOKEN = /^[A-G](#|b)?[A-Za-z0-9#b/+°ø()-]*$/;

export function isChordLine(line: string): boolean {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((t) => CHORD_TOKEN.test(t));
}

/** Read the plain-text chart format into paired lines. */
export function linesFromText(text: string): Line[] {
  const raw = text.split("\n");
  const lines: Line[] = [];

  for (let i = 0; i < raw.length; i += 1) {
    const above = raw[i];
    if (!above.trim()) continue;

    if (!isChordLine(above)) {
      // Words with nothing above them.
      if (/[A-Za-zÀ-ž]/.test(above)) lines.push({ text: above, chords: [] });
      continue;
    }

    const chords: Chord[] = [];
    const finder = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = finder.exec(above))) {
      chords.push({ at: match.index, name: match[0] });
    }

    const next = raw[i + 1] ?? "";
    const hasWords = next.trim() && !isChordLine(next) && /[A-Za-zÀ-ž]/.test(next);
    if (hasWords) {
      lines.push({ text: next, chords });
      i += 1;
    } else {
      lines.push({ text: "", chords, instrumental: true });
    }
  }
  return lines;
}

/** Write paired lines back out as the chart format. */
export function textFromLines(lines: Line[]): string {
  const out: string[] = [];
  for (const line of lines) {
    let above = "";
    for (const chord of [...line.chords].sort((a, b) => a.at - b.at)) {
      above = above.length > chord.at
        ? `${above} ${chord.name}`
        : above.padEnd(chord.at) + chord.name;
    }
    out.push(above);
    if (!line.instrumental) out.push(line.text);
    out.push("");
  }
  return out.join("\n").replace(/\n+$/, "\n");
}

/**
 * Replace `[from, to)` with `insert`, carrying the chords.
 *
 * A chord inside the replaced range has lost the word it belonged to, so it
 * moves to the start of whatever replaced it rather than being dropped — losing
 * a chord because a typo was fixed would be worse than a chord slightly out.
 */
export function editText(line: Line, from: number, to: number, insert: string): Line {
  const delta = insert.length - (to - from);
  return {
    ...line,
    text: line.text.slice(0, from) + insert + line.text.slice(to),
    chords: line.chords.map((chord) => {
      if (chord.at <= from) return chord;
      if (chord.at >= to) return { ...chord, at: chord.at + delta };
      return { ...chord, at: from + insert.length };
    }).sort((a, b) => a.at - b.at),
  };
}

/**
 * Join a line into the one above it.
 *
 * The joined line's chords shift right by the length of the first line plus the
 * space between them, so each chord stays over its own word.
 */
export function mergeUp(lines: Line[], index: number): { lines: Line[]; caret: number } {
  if (index <= 0 || index >= lines.length) return { lines, caret: 0 };
  const above = lines[index - 1];
  const here = lines[index];
  if (above.instrumental || here.instrumental) return { lines, caret: 0 };

  // Only invent a space when there is not already one at the seam. Splitting a
  // line leaves the head ending in a space, and adding another there would put
  // every chord on the joined line one character right — merging has to be the
  // exact inverse of splitting.
  const seamHasSpace = /\s$/.test(above.text) || /^\s/.test(here.text);
  const joiner = above.text && here.text && !seamHasSpace ? " " : "";
  const offset = above.text.length + joiner.length;
  const merged: Line = {
    text: above.text + joiner + here.text,
    chords: [
      ...above.chords,
      ...here.chords.map((chord) => ({ ...chord, at: chord.at + offset })),
    ].sort((a, b) => a.at - b.at),
  };
  return {
    lines: [...lines.slice(0, index - 1), merged, ...lines.slice(index + 1)],
    caret: offset,
  };
}

/**
 * Break a line at the caret. Chords past it move to the new line and lose the
 * offset, which is merging run backwards.
 */
export function splitAt(lines: Line[], index: number, caret: number): Line[] {
  const line = lines[index];
  if (!line || line.instrumental) return lines;
  const head: Line = {
    text: line.text.slice(0, caret),
    chords: line.chords.filter((c) => c.at < caret || (c.at === caret && caret === 0)),
  };
  const tail: Line = {
    text: line.text.slice(caret),
    chords: line.chords.filter((c) => c.at >= caret && caret > 0)
      .map((c) => ({ ...c, at: c.at - caret })),
  };
  return [...lines.slice(0, index), head, tail, ...lines.slice(index + 1)];
}

/** Slide a chord along its line, never past the ends. */
export function moveChord(line: Line, which: number, by: number): Line {
  const chords = line.chords.map((chord, index) =>
    index === which
      ? { ...chord, at: Math.max(0, Math.min(chord.at + by, Math.max(0, line.text.length))) }
      : chord);
  return { ...line, chords };
}

export function setChord(line: Line, which: number, name: string): Line {
  return {
    ...line,
    chords: line.chords.map((chord, i) => (i === which ? { ...chord, name } : chord)),
  };
}

export function removeChord(line: Line, which: number): Line {
  return { ...line, chords: line.chords.filter((_, i) => i !== which) };
}

/** Add a chord at a character position; returns the line and its index. */
export function insertChord(
  line: Line, at: number, name = "C",
): { line: Line; which: number } {
  const chords = [...line.chords, { at, name }].sort((a, b) => a.at - b.at);
  return { line: { ...line, chords }, which: chords.findIndex((c) => c.at === at) };
}

/** The chord nearest a caret position, for Tab. */
export function nearestChord(line: Line, caret: number): number {
  if (!line.chords.length) return -1;
  let best = 0;
  let distance = Infinity;
  line.chords.forEach((chord, index) => {
    const away = Math.abs(chord.at - caret);
    if (away < distance) { distance = away; best = index; }
  });
  return best;
}
